import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Search, ChevronLeft, ChevronRight, X, Plus, Trash2 } from 'lucide-react';

const RANK_BORDER = { normal:'#93c5fd', silver:'#cbd5e1', gold:'#fbbf24', platinum:'#1f2937', blacklist:'#f87171', new:'#4ade80' };
function getBorderColor(b) {
  if (b.customer_is_blacklisted) return RANK_BORDER.blacklist;
  if (b.customer_ranking && b.customer_ranking !== 'normal') return RANK_BORDER[b.customer_ranking] || RANK_BORDER.normal;
  if (b.customer_is_new) return RANK_BORDER.new;
  return RANK_BORDER.normal;
}
function copyText(t) { navigator.clipboard.writeText(t||'').then(()=>toast.success('Copied!')).catch(()=>{}); }

function Avatar({ b, size=32 }) {
  const border = getBorderColor(b);
  const st = { width:size, height:size, borderRadius:'50%', border:`2.5px solid ${border}`, flexShrink:0, objectFit:'cover', cursor:'pointer' };
  return b.customer_fb_profile_pic
    ? <img src={b.customer_fb_profile_pic} alt="" style={st} onClick={()=>copyText(b.customer_name)} title="Copy name" />
    : <div style={{...st, background:'linear-gradient(135deg,#7c6af5,#c084fc)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:size*0.38, fontWeight:700, color:'#fff'}}
        onClick={()=>copyText(b.customer_name)} title="Copy name">{(b.customer_name||'?')[0].toUpperCase()}</div>;
}

const STATUS_COLORS = {
  pending:   { bg:'#fef3c7', text:'#92400e', label:'Pending' },
  printed:   { bg:'#dbeafe', text:'#1d4ed8', label:'Printed' },
  packed:    { bg:'#e0f2fe', text:'#0369a1', label:'Packed' },
  shipped:   { bg:'#dcfce7', text:'#15803d', label:'Shipped' },
  delivered: { bg:'#f0fdf4', text:'#16a34a', label:'Delivered' },
  cancelled: { bg:'#fee2e2', text:'#dc2626', label:'Cancelled' },
};
const PAY_STATUS = {
  unpaid:   { bg:'#fee2e2', text:'#dc2626', label:'Unpaid' },
  partial:  { bg:'#fef3c7', text:'#92400e', label:'Partial' },
  paid:     { bg:'#dcfce7', text:'#16a34a', label:'Paid' },
  overpaid: { bg:'#ede9fe', text:'#7c3aed', label:'Overpaid' },
};
const DELIVERY_LABELS = { VET:'VET', JT_COD:'J&T COD', JT_NON_COD:'J&T Non COD', DOM:'DOM', SONA:'Sona', TAKE_SHOP:'Take Shop', GRAB:'Grab' };
const PROVINCE_TABS = ['VET','JT_NON_COD'];
const PAYMENT_METHODS = ['cash','aba_qr','aba_transfer','acleda','wings','true_money','emoney','western_union','non_cod','other'];

function PaymentPanel({ bill, onClose, onUpdated }) {
  const [payments, setPayments] = useState([]);
  const [form, setForm] = useState({ payment_method:'cash', currency:'USD', amount:'', transaction_id:'', notes:'' });
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({});
  const inp = { background:'#f8f9fa', border:'1px solid #dee2e6', borderRadius:6, padding:'6px 9px', fontSize:12, outline:'none', width:'100%', boxSizing:'border-box' };

  useEffect(() => {
    api.get(`/billing/bills/${bill.id}/payments`).then(r=>setPayments(r.data||[])).catch(()=>{});
    api.get('/settings/public').then(r=>setSettings(r.data||{})).catch(()=>{});
  }, [bill.id]);

  const totalPaid = payments.reduce((s,p)=>s+p.amount_usd,0);
  const remaining = parseFloat(bill.total||0) - totalPaid;
  const rate = parseFloat(settings.usd_khr_rate||4100);

  const save = async () => {
    if (!form.amount || parseFloat(form.amount)<=0) return toast.error('Enter amount');
    setSaving(true);
    try {
      await api.post(`/billing/bills/${bill.id}/payments`, form);
      toast.success('Payment recorded');
      const r = await api.get(`/billing/bills/${bill.id}/payments`);
      setPayments(r.data||[]);
      setForm({payment_method:'cash',currency:'USD',amount:'',transaction_id:'',notes:''});
      onUpdated();
    } catch(e) { toast.error(e.response?.data?.detail||'Failed'); }
    finally { setSaving(false); }
  };

  const del = async (pid) => {
    try {
      await api.delete(`/billing/bills/${bill.id}/payments/${pid}`);
      const r = await api.get(`/billing/bills/${bill.id}/payments`);
      setPayments(r.data||[]); onUpdated();
    } catch { toast.error('Failed'); }
  };

  const khrPreview = form.currency==='KHR' && form.amount ? `≈ $${(parseFloat(form.amount)/rate).toFixed(2)}` : form.currency==='USD' && form.amount ? `≈ ${(parseFloat(form.amount)*rate).toLocaleString()}៛` : '';

  return (
    <div style={{ marginTop:12, background:'#f9fafb', borderRadius:8, padding:14 }}>
      <div style={{ fontSize:12, fontWeight:700, color:'#374151', marginBottom:10 }}>💰 Payment Recording</div>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:8 }}>
        <span style={{ color:'#6b7280' }}>Bill Total: <strong>${parseFloat(bill.total||0).toFixed(2)}</strong></span>
        <span style={{ color:'#6b7280' }}>Paid: <strong style={{color:'#16a34a'}}>${totalPaid.toFixed(2)}</strong></span>
        <span style={{ color: remaining>0?'#dc2626':'#16a34a' }}>Remaining: <strong>${remaining.toFixed(2)}</strong></span>
      </div>
      {payments.map(p=>(
        <div key={p.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 10px', background:'#fff', borderRadius:6, marginBottom:6, border:'1px solid #e5e7eb', fontSize:12 }}>
          <div>
            <span style={{ fontWeight:600, color:'#374151' }}>{p.payment_method}</span>
            <span style={{ color:'#6b7280', marginLeft:8 }}>{p.currency==='KHR'?`${parseFloat(p.amount).toLocaleString()}៛`:`$${parseFloat(p.amount).toFixed(2)}`} → <strong>${parseFloat(p.amount_usd).toFixed(2)}</strong></span>
            {p.transaction_id&&<span style={{ color:'#9ca3af', marginLeft:6 }}>#{p.transaction_id}</span>}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ color:'#9ca3af', fontSize:11 }}>{p.created_at}</span>
            <button onClick={()=>del(p.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#dc2626' }}><Trash2 size={12}/></button>
          </div>
        </div>
      ))}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
        <div>
          <div style={{ fontSize:11, color:'#6b7280', marginBottom:3 }}>Method</div>
          <select value={form.payment_method} onChange={e=>setForm(f=>({...f,payment_method:e.target.value}))} style={inp}>
            {PAYMENT_METHODS.map(m=><option key={m} value={m}>{m.replace('_',' ').toUpperCase()}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize:11, color:'#6b7280', marginBottom:3 }}>Currency</div>
          <select value={form.currency} onChange={e=>setForm(f=>({...f,currency:e.target.value}))} style={inp}>
            <option value="USD">USD $</option>
            <option value="KHR">KHR ៛</option>
          </select>
        </div>
        <div>
          <div style={{ fontSize:11, color:'#6b7280', marginBottom:3 }}>Amount {khrPreview&&<span style={{color:'#7c6af5'}}>{khrPreview}</span>}</div>
          <input type="number" step="0.01" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))} style={inp} placeholder="0.00" />
        </div>
        <div>
          <div style={{ fontSize:11, color:'#6b7280', marginBottom:3 }}>Transaction ID</div>
          <input value={form.transaction_id} onChange={e=>setForm(f=>({...f,transaction_id:e.target.value}))} style={inp} placeholder="ABA ref..." />
        </div>
      </div>
      <div style={{ display:'flex', gap:8 }}>
        <button onClick={save} disabled={saving} style={{ flex:1, background:'#7c6af5', color:'#fff', border:'none', borderRadius:6, padding:'8px 0', fontSize:13, fontWeight:600, cursor:'pointer' }}>
          {saving?'Saving...':'+ Add Payment'}
        </button>
        <button onClick={onClose} style={{ background:'#f3f4f6', color:'#374151', border:'none', borderRadius:6, padding:'8px 14px', fontSize:13, cursor:'pointer' }}>Close</button>
      </div>
    </div>
  );
}

function TrackingPanel({ bill, onUpdated }) {
  const [tracking, setTracking] = useState(bill.tracking_number||'');
  const [saving, setSaving] = useState(false);
  const inp = { background:'#f8f9fa', border:'1px solid #dee2e6', borderRadius:6, padding:'6px 9px', fontSize:12, outline:'none', flex:1 };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/billing/bills/${bill.id}/tracking`, { tracking_number: tracking, jnt_tracking_no: tracking });
      toast.success('Tracking saved'); onUpdated();
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const dmText = () => {
    const lines = [`សួស្តី ${bill.customer_name} 👋`, ``, `កញ្ចប់របស់អ្នកត្រូវបានផ្ញើហើយ!`];
    if (tracking) lines.push(`លេខតាមដាន: ${tracking}`);
    if (PROVINCE_TABS.includes(bill.preferred_delivery)) {
      lines.push(``, `ស្វែងរកកញ្ចប់: https://www.jtexpresskh.com/track`);
    }
    lines.push(``, `សូមអរគុណ 🙏 123 Crystal`);
    return lines.join('\n');
  };

  return (
    <div style={{ marginTop:8 }}>
      <div style={{ fontSize:11, color:'#6b7280', marginBottom:4 }}>📦 Tracking Number</div>
      <div style={{ display:'flex', gap:6 }}>
        <input value={tracking} onChange={e=>setTracking(e.target.value)}
          onKeyDown={e=>e.key==='Enter'&&save()}
          style={inp} placeholder="J01375239116 or VET tracking..." />
        <button onClick={save} disabled={saving} style={{ background:'#7c6af5', color:'#fff', border:'none', borderRadius:6, padding:'5px 12px', fontSize:12, cursor:'pointer' }}>
          {saving?'...':'Save'}
        </button>
        {tracking&&<button onClick={()=>{navigator.clipboard.writeText(dmText()).then(()=>toast.success('Tracking DM copied!')).catch(()=>{});}}
          style={{ background:'#eff6ff', color:'#2563eb', border:'none', borderRadius:6, padding:'5px 10px', fontSize:12, cursor:'pointer' }}>
          📨 Copy DM
        </button>}
      </div>
    </div>
  );
}

function StatusModal({ bill, onClose, onUpdated }) {
  const [status, setStatus] = useState(bill.status);
  const [tracking, setTracking] = useState(bill.tracking_number||'');
  const [cancelReason, setCancelReason] = useState('');
  const [saving, setSaving] = useState(false);
  const FLOW = ['pending','printed','packed','shipped','delivered'];
  const inp = { background:'#f8f9fa', border:'1px solid #dee2e6', borderRadius:6, padding:'7px 10px', fontSize:13, outline:'none', width:'100%', boxSizing:'border-box' };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/billing/bills/${bill.id}/status`, { status, tracking_number:tracking, cancel_reason:cancelReason });
      toast.success('Status updated'); onUpdated();
    } catch(e) { toast.error(e.response?.data?.detail||'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:600, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:12, padding:24, width:380, boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <div style={{ fontWeight:700, fontSize:15, color:'#111827' }}>Update — {bill.bill_number}</div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#9ca3af' }}><X size={16}/></button>
        </div>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:16 }}>
          {FLOW.map(s=>{const sc=STATUS_COLORS[s];const active=status===s;return(
            <button key={s} onClick={()=>setStatus(s)} style={{ background:active?sc.bg:'#f3f4f6', color:active?sc.text:'#6b7280', border:`1px solid ${active?sc.text+'40':'#e5e7eb'}`, borderRadius:6, padding:'5px 12px', fontSize:12, fontWeight:active?700:400, cursor:'pointer' }}>{sc.label}</button>
          );})}
          <button onClick={()=>setStatus('cancelled')} style={{ background:status==='cancelled'?'#fee2e2':'#f3f4f6', color:status==='cancelled'?'#dc2626':'#6b7280', border:`1px solid ${status==='cancelled'?'#fca5a5':'#e5e7eb'}`, borderRadius:6, padding:'5px 12px', fontSize:12, cursor:'pointer' }}>Cancel</button>
        </div>
        {status==='shipped'&&<div style={{marginBottom:14}}><label style={{fontSize:11,fontWeight:600,color:'#6b7280',display:'block',marginBottom:4}}>Tracking Number</label><input value={tracking} onChange={e=>setTracking(e.target.value)} style={inp} placeholder="Optional..." /></div>}
        {status==='cancelled'&&<div style={{marginBottom:14}}><label style={{fontSize:11,fontWeight:600,color:'#6b7280',display:'block',marginBottom:4}}>Cancel Reason *</label><input value={cancelReason} onChange={e=>setCancelReason(e.target.value)} style={inp} placeholder="Reason..." /></div>}
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={onClose} style={{ flex:1, background:'#f3f4f6', color:'#374151', border:'none', borderRadius:8, padding:'9px 0', fontSize:13, cursor:'pointer' }}>Cancel</button>
          <button onClick={save} disabled={saving||(status==='cancelled'&&!cancelReason)} style={{ flex:1, background:'#7c6af5', color:'#fff', border:'none', borderRadius:8, padding:'9px 0', fontSize:13, fontWeight:600, cursor:'pointer' }}>{saving?'Saving...':'Update'}</button>
        </div>
      </div>
    </div>
  );
}

function BillDetail({ bill, onClose, onUpdated }) {
  const [detail, setDetail] = useState(null);
  const [showStatus, setShowStatus] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [localBill, setLocalBill] = useState(bill);

  useEffect(()=>{
    api.get(`/billing/bills/${bill.id}`).then(r=>{setDetail(r.data);setLocalBill(r.data);}).catch(()=>{});
  },[bill.id]);

  const refresh = () => {
    api.get(`/billing/bills/${bill.id}`).then(r=>{setDetail(r.data);setLocalBill(r.data);onUpdated();}).catch(()=>{});
  };

  const sc = STATUS_COLORS[localBill.status]||STATUS_COLORS.pending;
  const ps = PAY_STATUS[localBill.payment_status]||PAY_STATUS.unpaid;
  const phones = localBill.customer_phones?.length?localBill.customer_phones:[localBill.customer_phone].filter(Boolean);

  const buildDm = () => {
    const orders = detail?.orders||[];
    const lines = [`សួស្តី ${localBill.customer_name} 👋`,``,`សង្ខេបការបញ្ជាទិញរបស់អ្នក៖`];
    orders.forEach(o=>{
      const prod = o.product_code?`${o.product_code} x${o.quantity}`:`x${o.quantity}`;
      lines.push(`• ${o.order_number} — ${prod} — $${o.total.toFixed(2)}`);
    });
    lines.push(``,`សរុបរង៖ $${parseFloat(localBill.subtotal||0).toFixed(2)}`);
    if (parseFloat(localBill.delivery_fee||0)>0) lines.push(`ដឹកជញ្ជូន៖ $${parseFloat(localBill.delivery_fee).toFixed(2)}`);
    lines.push(`សរុបទាំងអស់៖ $${parseFloat(localBill.total||0).toFixed(2)}`);
    lines.push(``,`សូមអរគុណ 🙏 123 Crystal`);
    return lines.join('\n');
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:500, display:'flex', alignItems:'flex-start', justifyContent:'flex-end' }}>
      <div style={{ background:'#fff', width:440, height:'100vh', overflowY:'auto', boxShadow:'-4px 0 30px rgba(0,0,0,0.15)' }}>
        <div style={{ padding:'16px 20px', borderBottom:'1px solid #e5e7eb', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:'#fff', zIndex:1 }}>
          <div>
            <div style={{ fontFamily:'monospace', fontWeight:700, fontSize:15, color:'#7c6af5', cursor:'pointer' }} onClick={()=>copyText(localBill.bill_number)}>{localBill.bill_number}</div>
            <div style={{ fontSize:11, color:'#9ca3af' }}>{localBill.created_at}</div>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:8, background:sc.bg, color:sc.text }}>{sc.label}</span>
            <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:8, background:ps.bg, color:ps.text }}>{ps.label}</span>
            <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#9ca3af' }}><X size={18}/></button>
          </div>
        </div>

        <div style={{ padding:20 }}>
          {/* Customer */}
          <div style={{ display:'flex', gap:12, alignItems:'center', marginBottom:16, background:'#f9fafb', borderRadius:8, padding:'12px 14px' }}>
            <Avatar b={localBill} size={40} />
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontWeight:700, fontSize:14, color:'#111827' }}>{localBill.customer_name}</div>
              {phones.map((p,i)=><div key={i} style={{ fontSize:12, color:'#6b7280' }}>📞 {p}</div>)}
              {localBill.customer_address&&<div style={{ fontSize:12, color:'#6b7280' }}>📍 {localBill.customer_address}</div>}
              {localBill.preferred_delivery&&<div style={{ fontSize:11, color:'#0369a1', marginTop:2 }}>🚚 {DELIVERY_LABELS[localBill.preferred_delivery]||localBill.preferred_delivery}</div>}
            </div>
          </div>

          {/* Orders */}
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:600, color:'#6b7280', marginBottom:8, textTransform:'uppercase' }}>Orders</div>
            {detail?.orders?.map(o=>(
              <div key={o.id} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid #f3f4f6', fontSize:12 }}>
                <div>
                  <span style={{ fontFamily:'monospace', color:'#7c6af5', fontWeight:700, fontSize:11 }}>{o.order_number}</span>
                  {o.product_code&&<span style={{ marginLeft:8, color:'#374151' }}>{o.product_code} x{o.quantity}</span>}
                  {o.remarks&&<div style={{ color:'#9ca3af', fontSize:11 }}>{o.remarks}</div>}
                </div>
                <span style={{ fontWeight:700, color:'#16a34a' }}>${o.total.toFixed(2)}</span>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div style={{ background:'#f9fafb', borderRadius:8, padding:'12px 14px', marginBottom:12 }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'#6b7280', marginBottom:4 }}><span>Subtotal</span><span>${parseFloat(localBill.subtotal||0).toFixed(2)}</span></div>
            {parseFloat(localBill.delivery_fee||0)>0&&<div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'#6b7280', marginBottom:4 }}><span>Delivery</span><span>+${parseFloat(localBill.delivery_fee).toFixed(2)}</span></div>}
            {parseFloat(localBill.discount||0)>0&&<div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'#dc2626', marginBottom:4 }}><span>Discount</span><span>-${parseFloat(localBill.discount).toFixed(2)}</span></div>}
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:15, fontWeight:700, color:'#111827', borderTop:'1px solid #e5e7eb', paddingTop:8, marginTop:4 }}><span>Total</span><span>${parseFloat(localBill.total||0).toFixed(2)}</span></div>
            {localBill.staff_note&&<div style={{ fontSize:11, color:'#6b7280', marginTop:6 }}>📝 {localBill.staff_note}</div>}
          </div>

          {/* Tracking */}
          <TrackingPanel bill={localBill} onUpdated={refresh} />

          {/* Payment panel */}
          {showPayment
            ? <PaymentPanel bill={localBill} onClose={()=>setShowPayment(false)} onUpdated={refresh} />
            : <button onClick={()=>setShowPayment(true)} style={{ marginTop:10, width:'100%', background:'#f0fdf4', color:'#16a34a', border:'1px solid #bbf7d0', borderRadius:8, padding:'8px 0', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                💰 Record Payment
              </button>
          }

          {/* Timeline */}
          <div style={{ marginTop:16, marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:600, color:'#6b7280', marginBottom:8, textTransform:'uppercase' }}>Timeline</div>
            {[['Created',localBill.created_at],['Printed',localBill.printed_at],['Packed',localBill.packed_at],['Shipped',localBill.shipped_at],['Delivered',localBill.delivered_at]].map(([label,time])=>time?(
              <div key={label} style={{ display:'flex', gap:10, fontSize:12, marginBottom:4 }}>
                <span style={{ color:'#9ca3af', width:60 }}>{label}</span>
                <span style={{ color:'#374151' }}>{time}</span>
              </div>
            ):null)}
            {localBill.cancel_reason&&<div style={{ fontSize:12, color:'#dc2626' }}>❌ {localBill.cancel_reason}</div>}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding:'14px 20px', borderTop:'1px solid #e5e7eb', display:'flex', gap:8, position:'sticky', bottom:0, background:'#fff' }}>
          <button onClick={()=>setShowStatus(true)} style={{ flex:1, background:'#7c6af5', color:'#fff', border:'none', borderRadius:8, padding:'10px 0', fontSize:13, fontWeight:600, cursor:'pointer' }}>Update Status</button>
            <button onClick={()=>window.open('https://123cambodia.newgads.com/print/'+localBill.id,'_blank')} style={{ background:'#f3f4f6', color:'#374151', border:'1px solid #d1d5db', borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:600, cursor:'pointer' }}>🖨️ Print</button>
          <button onClick={()=>{navigator.clipboard.writeText(buildDm()).then(()=>toast.success('Order DM copied!')).catch(()=>{});}} style={{ flex:1, background:'#eff6ff', color:'#2563eb', border:'1px solid #bfdbfe', borderRadius:8, padding:'10px 0', fontSize:13, fontWeight:600, cursor:'pointer' }}>📋 Copy DM</button>
        </div>
      </div>
      {showStatus&&<StatusModal bill={localBill} onClose={()=>setShowStatus(false)} onUpdated={()=>{setShowStatus(false);refresh();}} />}
    </div>
  );
}

export default function Bills() {
  const [bills, setBills] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [checkedBills, setCheckedBills] = useState([]);
  const toggleCheck = (e, id) => {
    e.stopPropagation();
    setCheckedBills(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);
  };
  const toggleAll = () => {
    setCheckedBills(prev => prev.length===bills.length ? [] : bills.map(b=>b.id));
  };
  const printSelected = () => {
    if (!checkedBills.length) return;
    const url = 'https://123cambodia.newgads.com/print/multi?ids='+checkedBills.join(',');
    window.open(url, '_blank');
  };


  const [filterStatus, setFilterStatus] = useState('');
  const [filterPayStatus, setFilterPayStatus] = useState('');
  const [filterDelivery, setFilterDelivery] = useState('');
  const [filterQ, setFilterQ] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  useEffect(()=>{ load(); },[page]);

  async function load(params={}) {
    setLoading(true);
    try {
      const r = await api.get('/billing/bills', { params: { page, per_page:50, status:params.status??filterStatus, search:params.search??filterQ, delivery:params.delivery??filterDelivery, date_from:params.date_from??filterDateFrom, date_to:params.date_to??filterDateTo }});
      setBills(r.data.bills||[]); setTotal(r.data.total||0); setPages(r.data.pages||1);
    } catch { toast.error('Failed to load bills'); }
    finally { setLoading(false); }
  }

  const inp = { background:'#f8f9fa', border:'1px solid #dee2e6', borderRadius:6, padding:'6px 10px', fontSize:13, outline:'none' };

  return (
    <div style={{ padding:24, minHeight:'100vh', background:'#f8fafc' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h1 style={{ margin:0, fontSize:22, fontWeight:700, color:'#111827' }}>🧾 Bills</h1>
          <p style={{ margin:'2px 0 0', fontSize:13, color:'#6b7280' }}>{total.toLocaleString()} bills</p>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          {checkedBills.length>0&&<button onClick={printSelected} style={{ background:'#7c6af5', color:'#fff', border:'none', borderRadius:8, padding:'9px 18px', fontSize:13, fontWeight:600, cursor:'pointer' }}>🖨️ Print {checkedBills.length} Bill{checkedBills.length>1?'s':''}</button>}
          {checkedBills.length>0&&<button onClick={()=>setCheckedBills([])} style={{ background:'#f3f4f6', color:'#374151', border:'1px solid #d1d5db', borderRadius:8, padding:'9px 14px', fontSize:13, cursor:'pointer' }}>✕ Clear</button>}
        </div>
      </div>

      {/* Filters */}
      <div style={{ background:'#fff', borderRadius:8, border:'1px solid #e5e7eb', padding:'12px 14px', marginBottom:16, display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end' }}>
        <div style={{ position:'relative', flex:'1 1 180px' }}>
          <Search size={13} style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', color:'#9ca3af' }} />
          <input value={filterQ} onChange={e=>setFilterQ(e.target.value)} onKeyDown={e=>e.key==='Enter'&&(setPage(1),load())}
            placeholder="Bill no, customer..." style={{ ...inp, paddingLeft:28, width:'100%' }} />
        </div>
        <div style={{ flex:'0 0 120px' }}>
          <div style={{ fontSize:11, fontWeight:600, color:'#6b7280', marginBottom:3 }}>STATUS</div>
          <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{ ...inp, width:'100%' }}>
            <option value="">All</option>
            {Object.entries(STATUS_COLORS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div style={{ flex:'0 0 120px' }}>
          <div style={{ fontSize:11, fontWeight:600, color:'#6b7280', marginBottom:3 }}>PAYMENT</div>
          <select value={filterPayStatus} onChange={e=>setFilterPayStatus(e.target.value)} style={{ ...inp, width:'100%' }}>
            <option value="">All</option>
            {Object.entries(PAY_STATUS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div style={{ flex:'0 0 130px' }}>
          <div style={{ fontSize:11, fontWeight:600, color:'#6b7280', marginBottom:3 }}>DELIVERY</div>
          <select value={filterDelivery} onChange={e=>setFilterDelivery(e.target.value)} style={{ ...inp, width:'100%' }}>
            <option value="">All</option>
            {Object.entries(DELIVERY_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div style={{ flex:'0 0 120px' }}>
          <div style={{ fontSize:11, fontWeight:600, color:'#6b7280', marginBottom:3 }}>FROM</div>
          <input type="date" value={filterDateFrom} onChange={e=>setFilterDateFrom(e.target.value)} style={{ ...inp, width:'100%' }} />
        </div>
        <div style={{ flex:'0 0 120px' }}>
          <div style={{ fontSize:11, fontWeight:600, color:'#6b7280', marginBottom:3 }}>TO</div>
          <input type="date" value={filterDateTo} onChange={e=>setFilterDateTo(e.target.value)} style={{ ...inp, width:'100%' }} />
        </div>
        <button onClick={()=>{setPage(1);load();}} style={{ background:'#2563eb', color:'#fff', border:'none', borderRadius:6, padding:'7px 18px', fontSize:13, fontWeight:600, cursor:'pointer' }}>Search</button>
        {(filterQ||filterStatus||filterPayStatus||filterDelivery||filterDateFrom||filterDateTo)&&
          <button onClick={()=>{setFilterQ('');setFilterStatus('');setFilterPayStatus('');setFilterDelivery('');setFilterDateFrom('');setFilterDateTo('');setPage(1);load({status:'',search:'',delivery:'',date_from:'',date_to:''});}}
            style={{ background:'#fee2e2', color:'#dc2626', border:'none', borderRadius:6, padding:'7px 12px', fontSize:13, cursor:'pointer' }}>✕</button>}
      </div>

      {/* Table */}
      <div style={{ background:'#fff', borderRadius:8, border:'1px solid #e5e7eb', overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:'#f9fafb', borderBottom:'1px solid #e5e7eb' }}>
              <th style={{ padding:'8px 12px', width:36 }}><input type='checkbox' checked={checkedBills.length===bills.length&&bills.length>0} onChange={toggleAll} /></th>
              {['Bill No','Customer','Delivery','Subtotal','Total','Status','Payment','Created'].map(h=>(
                <th key={h} style={{ padding:'10px 12px', textAlign:'left', fontWeight:600, color:'#374151', fontSize:12, whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading&&<tr><td colSpan={9} style={{ padding:40, textAlign:'center', color:'#9ca3af' }}>Loading...</td></tr>}
            {!loading&&bills.length===0&&<tr><td colSpan={9} style={{ padding:40, textAlign:'center', color:'#9ca3af' }}>No bills found</td></tr>}
            {bills.map(bill=>{
              const sc=STATUS_COLORS[bill.status]||STATUS_COLORS.pending;
              const ps=PAY_STATUS[bill.payment_status]||PAY_STATUS.unpaid;
              return (
                <tr key={bill.id} style={{ borderBottom:'1px solid #f3f4f6', cursor:'pointer', background:checkedBills.includes(bill.id)?'#f5f3ff':'' }}
                  onMouseEnter={e=>{ if(!checkedBills.includes(bill.id)) e.currentTarget.style.background='#fafafa'; }}
                  onMouseLeave={e=>{ e.currentTarget.style.background=checkedBills.includes(bill.id)?'#f5f3ff':''; }}
                  onClick={()=>setSelected(bill)}>
                  <td style={{ padding:'8px 12px', width:36 }} onClick={e=>toggleCheck(e,bill.id)}>
                    <input type='checkbox' checked={checkedBills.includes(bill.id)} onChange={()=>{}} />
                  </td>
                  <td style={{ padding:'8px 12px' }}>
                    <div style={{ fontFamily:'monospace', fontWeight:700, color:'#7c6af5', fontSize:12 }}>{bill.bill_number}</div>
                    {bill.tracking_number&&<div style={{ fontSize:10, color:'#9ca3af' }}>📦 {bill.tracking_number}</div>}
                  </td>
                  <td style={{ padding:'8px 12px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <Avatar b={bill} size={28} />
                      <div>
                        <div style={{ fontSize:13, fontWeight:500, color:'#111827' }}>{bill.customer_name}</div>
                        <div style={{ fontSize:11, color:'#6b7280' }}>{bill.customer_phones?.length?bill.customer_phones[0]:bill.customer_phone||''}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding:'8px 12px' }}>
                    <span style={{ fontSize:11, background:'#e0f2fe', color:'#0369a1', padding:'2px 7px', borderRadius:8, fontWeight:600 }}>
                      {DELIVERY_LABELS[bill.preferred_delivery]||bill.delivery_category||'—'}
                    </span>
                  </td>
                  <td style={{ padding:'8px 12px', color:'#374151', fontWeight:600 }}>${parseFloat(bill.subtotal||0).toFixed(2)}</td>
                  <td style={{ padding:'8px 12px', fontWeight:700, color:'#16a34a' }}>${parseFloat(bill.total||0).toFixed(2)}</td>
                  <td style={{ padding:'8px 12px' }}>
                    <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:10, background:sc.bg, color:sc.text }}>{sc.label}</span>
                  </td>
                  <td style={{ padding:'8px 12px' }}>
                    <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:10, background:ps.bg, color:ps.text }}>{ps.label}</span>
                    {parseFloat(bill.amount_paid_usd||0)>0&&<div style={{ fontSize:10, color:'#9ca3af' }}>${parseFloat(bill.amount_paid_usd).toFixed(2)} paid</div>}
                  </td>
                  <td style={{ padding:'8px 12px', fontSize:11, color:'#9ca3af', whiteSpace:'nowrap' }}>{bill.created_at}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {pages>1&&(
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:12, padding:14, borderTop:'1px solid #e5e7eb' }}>
            <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} style={{ background:'none', border:'1px solid #d1d5db', borderRadius:6, padding:'6px 8px', cursor:'pointer' }}><ChevronLeft size={14}/></button>
            <span style={{ fontSize:13, color:'#6b7280' }}>Page {page} of {pages}</span>
            <button onClick={()=>setPage(p=>Math.min(pages,p+1))} disabled={page===pages} style={{ background:'none', border:'1px solid #d1d5db', borderRadius:6, padding:'6px 8px', cursor:'pointer' }}><ChevronRight size={14}/></button>
          </div>
        )}
      </div>
      {selected&&<BillDetail bill={selected} onClose={()=>setSelected(null)} onUpdated={()=>{setSelected(null);load();}} />}
    </div>
  );
}
